import braintree from 'braintree-web';
import { BRAINTREE_FIELD_IDS, BRAINTREE_DOM_READY_DELAY, BRAINTREE_HOSTED_FIELD_NAMES } from '@/constants/braintree';
import type { BraintreeCreditCardRequest } from '@/types/payment';
import type { SavedCard } from '@/types/checkout';
import type { HostedFieldsHostedFieldsFieldName } from 'braintree-web/hosted-fields';

export const waitForBraintreeDOMElements = async (): Promise<boolean> => {
  const cardNumberEl = document.querySelector(`#${BRAINTREE_FIELD_IDS.CARD_NUMBER}`);
  if (cardNumberEl) return true;

  await new Promise((resolve) => setTimeout(resolve, BRAINTREE_DOM_READY_DELAY));
  const retryEl = document.querySelector(`#${BRAINTREE_FIELD_IDS.CARD_NUMBER}`);
  return !!retryEl;
};

export const createBraintreeClient = async (authorization: string) => {
  return await braintree.client.create({ authorization });
};

export const createBraintreeHostedFields = async (
  clientInstance: braintree.Client,
  styles: Record<string, Record<string, string>>,
  fields: Record<string, { selector: string; placeholder: string }>
) => {
  return await braintree.hostedFields.create({
    client: clientInstance,
    styles,
    fields,
  });
};

export const teardownBraintreeHostedFields = async (hostedFields: braintree.HostedFields | null): Promise<void> => {
  if (!hostedFields) return;

  try {
    await hostedFields.teardown();
  } catch {
    // Ignore teardown errors
  }
};

export const isBraintreeError = (error: unknown): error is { code?: string; message?: string } => {
  return typeof error === 'object' && error !== null && 'code' in error;
};

export const buildBraintreeCardPaymentRequest = (
  selectedCard: SavedCard,
  shouldSaveCard: boolean,
  internalPaymentMethodId: string,
  receipt_email: string,
  forterTokenCookie: string = ''
): BraintreeCreditCardRequest => {
  return {
    paymentProvider: 'braintree',
    receipt_email,
    paymentMethodDisplayName: 'Credit Card',
    paymentMethodId: internalPaymentMethodId,
    params: {
      paymentNonce: selectedCard.pspCardIdentifier ?? '',
      cardHolderName: selectedCard.nameOnCard ?? selectedCard.cardholderName ?? '',
      shouldSaveCard,
      cardDetails: {
        bin: selectedCard.bin ?? '',
        expirationMonth: selectedCard.expiryMonth ?? '',
        expirationYear: selectedCard.expiryYear ?? '',
        lastFourDigits: selectedCard.cardLastFourDigits ?? '',
        nameOnCard: selectedCard.nameOnCard ?? selectedCard.cardholderName ?? '',
        cardType: selectedCard.brand ?? '',
      },
      fraudDetection: {
        forterTokenCookie,
      },
    },
  };
};

export const getNextBraintreeHostedFieldForAutoAdvance = (
  emittedBy: string
): HostedFieldsHostedFieldsFieldName | null => {
  if (emittedBy === BRAINTREE_HOSTED_FIELD_NAMES.CARD_NUMBER) {
    return BRAINTREE_HOSTED_FIELD_NAMES.EXPIRATION_DATE;
  }

  if (emittedBy === BRAINTREE_HOSTED_FIELD_NAMES.EXPIRATION_DATE) {
    return BRAINTREE_HOSTED_FIELD_NAMES.CVV;
  }

  return null;
};
